import { useState } from "react";
import { api, parseApiError } from "../lib/api";
import { Search, Loader2 } from "lucide-react";
import ErrorMessage from "./ErrorMessage";

export default function LookupBar() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.get("/booking/reservations/lookup", {
        params: { email, confirmationCode: code },
      });
      setResult(r.data);
    } catch (e) {
      setError(parseApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const payBalance = async () => {
    const rid =
      result?.reservation?.reservationID ??
      result?.reservation?.reservationId ??
      result?.reservationId;
    if (!rid) return;
    setBusy(true);
    try {
      const r = await api.post("/booking/payments/checkout", {
        reservationId: String(rid),
        type: "deposit",
      });
      if (r.data?.checkoutUrl) window.location.assign(r.data.checkoutUrl);
      else setError("RA did not return a checkout URL.");
    } catch (e) {
      setError(parseApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="lookup-section" className="aq-bg-navy" style={{ padding: "48px 0" }}>
      <div className="aq-container">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <span className="aq-overline">Find My Reservation</span>
            <h3 className="aq-h2 text-2xl sm:text-3xl mt-3" style={{ color: "var(--aq-foam)" }}>
              Already booked? Look it up.
            </h3>
            <p className="text-sm mt-3" style={{ color: "rgba(246, 244, 238, 0.7)" }}>
              Use the email + confirmation code from your booking confirmation. You can pay
              your balance, review trip details, or contact the crew.
            </p>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 grid sm:grid-cols-12 gap-3" data-testid="lookup-form">
            <input
              type="email"
              required
              placeholder="Email"
              data-testid="lookup-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="aq-input sm:col-span-5"
            />
            <input
              type="text"
              required
              placeholder="Confirmation code"
              data-testid="lookup-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="aq-input sm:col-span-4"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="lookup-submit"
              className="aq-btn-primary sm:col-span-3"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              {busy ? "Looking…" : "Look up"}
            </button>

            {result && (
              <div className="sm:col-span-12 mt-2 p-5 rounded-sm" style={{ background: "rgba(246,244,238,0.06)", border: "1px solid rgba(201,162,74,0.3)" }} data-testid="lookup-result">
                <p className="text-[13px]" style={{ color: "var(--aq-foam)" }}>
                  <strong>Held — deposit not yet paid.</strong> · {result?.reservation?.confirmationCode || "—"} · {result?.reservation?.renterFirstName || ""} {result?.reservation?.renterLastName || ""}
                </p>
                <p className="text-[12px] mt-1" style={{ color: "rgba(246,244,238,0.65)" }}>
                  Your spot stays on hold until the deposit is paid. Click below to complete payment now.
                </p>
                <button type="button" className="aq-btn-primary mt-3" onClick={payBalance} disabled={busy} data-testid="lookup-pay-balance">
                  Pay Deposit Now
                </button>
              </div>
            )}
            {error && (
              <div className="sm:col-span-12 mt-2" data-testid="lookup-error-wrap">
                <ErrorMessage testid="lookup-error" message={error} />
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
